import { useRef, useEffect, useState, useCallback, Suspense, lazy } from "react";
import type { EnrichedProject } from "./types/index.js";

/**
 * Filters a list of projects by a case-insensitive substring match on `project.name`.
 * Whitespace-only queries are treated as empty (returns the full list).
 * Special characters in the query are treated as literals (no regex interpretation).
 * Returns a new array; does not mutate the input.
 */
export function filterProjects(
	projects: EnrichedProject[],
	query: string,
): EnrichedProject[] {
	const trimmed = query.trim();
	if (trimmed === "") return projects;
	const lower = trimmed.toLowerCase();
	return projects.filter((p) => p.name.toLowerCase().includes(lower));
}

/**
 * Determines whether the Project Setup Wizard should be shown for the
 * currently selected project.
 *
 * Returns true when:
 *   - A project is selected (non-null)
 *   - That project has no linked WorkDrive folder (workdriveFolderId === null)
 *   - The user has not dismissed the wizard for this project in the current session
 */
export function shouldShowSetupWizard(
	selectedProject: EnrichedProject | null,
	wizardDismissedIds: Set<string>,
): boolean {
	return (
		selectedProject != null &&
		selectedProject.workdriveFolderId === null &&
		!wizardDismissedIds.has(selectedProject.id)
	);
}

/**
 * Determines whether the /dashboard command should activate the dashboard view.
 *
 * Returns true when the command is exactly "/dashboard" and a project is selected.
 */
export function shouldShowDashboard(
	command: string,
	selectedProjectId: string | null,
): boolean {
	return command === "/dashboard" && selectedProjectId != null;
}

// Dev-only chart test harness — zero production footprint (lazy + DEV guard)
const ChartTestHarness = lazy(() => import("./dev/ChartTestHarness.js"));
import ChatInput from "./components/ChatInput.js";
import ChatHeader from "./components/chat/ChatHeader.js";
import ChatMessages from "./components/chat/ChatMessages.js";
import ProjectDashboard from "./components/ProjectDashboard.js";
import LoginScreen from "./components/LoginScreen.js";
import ProjectList from "./components/ProjectList.js";
import ProjectSwitcher from "./components/ProjectSwitcher.js";
import ChatSessionList from "./components/ChatSessionList.js";
import NewChatTitleInput from "./components/NewChatTitleInput.js";
import UserInfo from "./components/UserInfo.js";
import ContextWarning from "./components/ContextWarning.js";
import IngestionBanner from "./components/IngestionBanner.js";
import ScopeBanner from "./components/ScopeBanner.js";
import ProjectSetupWizard from "./components/ProjectSetupWizard.js";
// ScrollButtons is now rendered inside ChatMessages — no direct import needed.
import PrimaryNav from "./components/sidebar/PrimaryNav.js";
import PortfolioReport from "./pages/PortfolioReport.js";
import { useAuth } from "./hooks/useAuth.js";
import { useProjects } from "./hooks/useProjects.js";
import { useChatSessions } from "./hooks/useChatSessions.js";
import { useChat } from "./hooks/useChat.js";
import { useIngestionStatus } from "./hooks/useIngestionStatus.js";
import { apiPatch } from "./lib/api.js";
import { isDigestSession } from "./lib/digest.js";
import DigestReadOnlyFooter from "./components/DigestReadOnlyFooter.js";

export default function App() {
	const { user, status, authError, login, logout } = useAuth();

	const {
		projects,
		selectedProjectId,
		loading: projectsLoading,
		error: projectsError,
		selectProject,
		refreshProjects,
	} = useProjects(status === "authenticated", user?.zohoUserId);

	const {
		sessions,
		activeSessionId,
		loading: sessionsLoading,
		createSession,
		selectSession,
		deleteSession,
		refreshSessions,
	} = useChatSessions(selectedProjectId);

	// Find the selected project to check whether a WorkDrive folder is linked.
	const selectedProject = projects.find((p) => p.id === selectedProjectId);

	const { status: ingestionStatus } = useIngestionStatus(
		selectedProjectId,
		!!selectedProject?.workdriveFolderId,
	);

	const { messages, isStreaming, contextWarning, sendMessage, clearContextWarning, handleApproveAction, handleRejectAction, justLoaded, resetJustLoaded } = useChat(
		selectedProjectId,
		activeSessionId,
		user?.displayName ?? null,
		user?.zohoUserId ?? null,
		{
			onSessionNeeded: createSession,
			onMessagesSaved: refreshSessions,
			onSessionIdChange: selectSession,
		},
	);

	/** IDs of projects whose setup wizard the user has dismissed this session. */
	const [wizardDismissedIds, setWizardDismissedIds] = useState<Set<string>>(new Set());

	/** True when the "Name your chat" step is visible (user clicked "New Chat"). */
	const [pendingNewChat, setPendingNewChat] = useState(false);
	/** True while the create + patch API calls are in-flight. */
	const [isCreatingSession, setIsCreatingSession] = useState(false);
	/** Controls the mobile sidebar drawer (hidden on desktop via CSS). */
	const [sidebarOpen, setSidebarOpen] = useState(false);

	/** True when the project dashboard is displayed instead of the chat. */
	const [showDashboard, setShowDashboard] = useState(false);

	/**
	 * Top-level view shown in the main content area: the normal chat/project
	 * flow, or the internal-staff-only Portfolio Health Report. Driven by the
	 * PrimaryNav sidebar rail.
	 */
	const [activeView, setActiveView] = useState<"chat" | "portfolio">("chat");

	/**
	 * Handles PrimaryNav's onSetView. "Dashboard" is not yet wired to a
	 * distinct top-level view here — the existing "/dashboard" slash command
	 * remains the supported entry point for the project dashboard — so it
	 * falls back to the normal chat flow, same as "chat".
	 */
	const handleSetView = useCallback((view: "chat" | "dashboard" | "portfolio") => {
		setActiveView(view === "portfolio" ? "portfolio" : "chat");
	}, []);

	/**
	 * True immediately after the user triggers a sync, before the first status
	 * poll confirms pending documents. Shows a "Starting sync…" banner state
	 * so there is instant visual feedback rather than a silent delay.
	 */
	const [isSyncStarting, setIsSyncStarting] = useState(false);

	// Clear isSyncStarting once real ingestion status arrives with pending docs.
	useEffect(() => {
		if (isSyncStarting && ingestionStatus?.hasPendingDocuments) {
			setIsSyncStarting(false);
		}
	}, [isSyncStarting, ingestionStatus]);

	// Safety fallback: clear isSyncStarting after 30 seconds in case the status
	// poll doesn't return pending documents (e.g. the sync completed very quickly).
	useEffect(() => {
		if (!isSyncStarting) return;
		const timer = setTimeout(() => setIsSyncStarting(false), 30_000);
		return () => clearTimeout(timer);
	}, [isSyncStarting]);

	const messagesEndRef = useRef<HTMLDivElement>(null);

	/** Ref to the scrollable messages container — used for scroll button logic. */
	const chatMessagesRef = useRef<HTMLDivElement>(null);

	/** Ref to the chat textarea — used to restore focus after streaming ends. */
	const chatInputRef = useRef<HTMLTextAreaElement>(null);

	/** Controls visibility of the scroll-to-top button. */
	const [showScrollTop, setShowScrollTop] = useState(false);
	/** Controls visibility of the scroll-to-bottom button. */
	const [showScrollBottom, setShowScrollBottom] = useState(false);

	/** Ref for throttling the scroll event handler (~100ms). */
	const scrollThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	/** Track previous streaming state to detect the transition from true → false. */
	const prevIsStreamingRef = useRef(false);

	// Auto-scroll to bottom when messages change.
	// When a session is freshly loaded (justLoaded), use requestAnimationFrame +
	// instant scroll so the position is set after the DOM has painted the full
	// message list. For streaming appends, keep the smooth behaviour.
	useEffect(() => {
		if (justLoaded) {
			requestAnimationFrame(() => {
				messagesEndRef.current?.scrollIntoView({ behavior: "instant", block: "end" });
			});
			resetJustLoaded();
		} else {
			messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
		}
	}, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

	// Re-focus the chat input when streaming completes
	useEffect(() => {
		if (prevIsStreamingRef.current && !isStreaming) {
			// Small delay lets React flush the DOM update (disabled state lifted) before focus
			requestAnimationFrame(() => {
				chatInputRef.current?.focus();
			});
		}
		prevIsStreamingRef.current = isStreaming;
	}, [isStreaming]);

	// Reset dashboard view when the selected project changes.
	useEffect(() => {
		setShowDashboard(false);
	}, [selectedProjectId]);

	/**
	 * Command handler passed to ChatInput via the onCommand prop.
	 * Returns true if the command was consumed (i.e. recognised and handled).
	 */
	const handleCommand = useCallback(
		(command: string): boolean => {
			if (shouldShowDashboard(command, selectedProjectId ?? null)) {
				setShowDashboard(true);
				return true;
			}
			return false;
		},
		[selectedProjectId],
	);

	/**
	 * Called when the user clicks "New Chat" in the sidebar.
	 * Shows the title-input step instead of immediately opening a blank chat.
	 */
	const handleNewChat = useCallback(() => {
		if (isStreaming) return;
		setPendingNewChat(true);
		selectSession(null);
	}, [isStreaming, selectSession]);

	/**
	 * Called when the user submits the new-chat title.
	 * Creates a session, patches its title, then switches into normal chat mode.
	 */
	const handleTitleSubmit = useCallback(
		async (title: string) => {
			setIsCreatingSession(true);
			try {
				const newId = await createSession(selectedProjectId);
				await apiPatch(`/api/chat/sessions/${newId}`, { title });
				setPendingNewChat(false);
				selectSession(newId);
				refreshSessions();
			} finally {
				setIsCreatingSession(false);
			}
		},
		[selectedProjectId, createSession, selectSession, refreshSessions],
	);

	/**
	 * Called when the user dismisses the title-input step (Escape or Cancel).
	 */
	const handleTitleCancel = useCallback(() => {
		setPendingNewChat(false);
	}, []);

	/** Throttled scroll handler — updates showScrollTop / showScrollBottom at ~100ms intervals. */
	const handleChatScroll = useCallback(() => {
		if (scrollThrottleRef.current) return;
		scrollThrottleRef.current = setTimeout(() => {
			scrollThrottleRef.current = null;
			const el = chatMessagesRef.current;
			if (!el) return;
			setShowScrollTop(el.scrollTop > 200);
			setShowScrollBottom(el.scrollHeight - el.scrollTop - el.clientHeight > 100);
		}, 100);
	}, []);

	/** Close the mobile drawer. */
	const closeSidebar = useCallback(() => setSidebarOpen(false), []);

	/** Dismiss the setup wizard for the current project (session-scoped). */
	const handleWizardSkip = useCallback(() => {
		if (selectedProjectId) {
			setWizardDismissedIds((prev) => new Set(prev).add(selectedProjectId));
		}
	}, [selectedProjectId]);

	/** Called when the wizard completes — dismiss immediately to prevent re-flash, then refresh. */
	const handleWizardComplete = useCallback(() => {
		if (selectedProjectId) {
			setWizardDismissedIds((prev) => new Set(prev).add(selectedProjectId));
		}
		refreshProjects();
	}, [selectedProjectId, refreshProjects]);

	/** Select a project and auto-close the mobile sidebar. */
	const handleProjectSelect = useCallback(
		(id: string | null) => {
			selectProject(id);
			closeSidebar();
		},
		[selectProject, closeSidebar],
	);

	/** Select a session and auto-close the mobile sidebar. */
	const handleSessionSelect = useCallback(
		(id: string | null) => {
			selectSession(id);
			closeSidebar();
		},
		[selectSession, closeSidebar],
	);

	/** Open "New Chat" and auto-close the mobile sidebar. */
	const handleNewChatMobile = useCallback(() => {
		handleNewChat();
		closeSidebar();
	}, [handleNewChat, closeSidebar]);

	// Dev-only route: navigate to #/chart-test to load the chart test harness.
	// Placed after ALL hooks to satisfy the Rules of Hooks (no conditional hook calls).
	// The import.meta.env.DEV guard ensures this code path never runs in production.
	if (import.meta.env.DEV && window.location.hash === "#/chart-test") {
		return (
			<Suspense fallback={<div style={{ padding: "24px", fontFamily: "sans-serif" }}>Loading chart test harness...</div>}>
				<ChartTestHarness />
			</Suspense>
		);
	}

	// --- Loading state ---
	if (status === "loading") {
		return (
			<div className="app-loading">
				<div className="loading-spinner" />
				<p>Loading...</p>
			</div>
		);
	}

	// --- Unauthenticated / error → login screen ---
	if (status === "unauthenticated" || status === "error") {
		return <LoginScreen onLogin={login} authError={authError} />;
	}

	// Determine the empty state message based on context
	const emptyStateMessage = selectedProjectId
		? "Start a new conversation or select a previous chat."
		: "Select a project from the sidebar, then ask a question.";

	// Derive the active chat session for ChatHeader metadata
	const currentSession = sessions.find((s) => s.id === activeSessionId) ?? null;

	// Digest sessions are read-only — ChatInput must not render for them.
	const activeSessionIsDigest = currentSession != null && isDigestSession(currentSession);

	// --- Authenticated app ---
	return (
		<div className="app">
			{/* Mobile backdrop — tapping it closes the drawer */}
			<div
				className={`sidebar-backdrop${sidebarOpen ? " sidebar-backdrop--visible" : ""}`}
				onClick={closeSidebar}
				aria-hidden="true"
			/>

			{/* Sidebar */}
			<aside className={`sidebar${sidebarOpen ? " sidebar--open" : ""}`}>
				<div className="sidebar-header">
					<img
						src="/caldere-logo.png"
						alt="Caldere Associates"
						className="sidebar-caldere-logo"
					/>
					<h1 className="logo">PIA</h1>
					<span className="logo-subtitle">Project Intelligence Assistant</span>
				</div>

				{user?.isInternalStaff && (
					<PrimaryNav
						view={activeView}
						onSetView={handleSetView}
						unreadCount={0}
						isInternalStaff={user.isInternalStaff}
					/>
				)}

				<div className="sidebar-body">
					{!selectedProjectId ? (
						/* State A: no project selected — scrollable project list */
						<>
							<div className="project-list-scroll">
								<ProjectList
									projects={projects}
									selectedProjectId={selectedProjectId}
									loading={projectsLoading}
									error={projectsError}
									onSelect={handleProjectSelect}
									onFolderLinked={refreshProjects}
								/>
							</div>
						</>
					) : (
						/* State B: project selected — compact switcher + chat sessions */
						<>
							<ProjectSwitcher
								projects={projects}
								selectedProjectId={selectedProjectId}
								onSelect={handleProjectSelect}
								onSettingsAction={refreshProjects}
								lastIngestedAt={ingestionStatus?.lastIngestedAt ?? null}
								isAdmin={user?.isAdmin ?? false}
								onSyncStarting={() => setIsSyncStarting(true)}
								onSyncFailed={() => setIsSyncStarting(false)}
							/>
							<nav aria-label="Chats" className="chat-section">
								<div className="sidebar-section-header" aria-hidden="true">Chats</div>
								<ChatSessionList
									sessions={sessions}
									activeSessionId={activeSessionId}
									loading={sessionsLoading}
									onSelect={handleSessionSelect}
									onDelete={deleteSession}
									onNewChat={handleNewChatMobile}
									isStreaming={isStreaming}
									currentUserId={user?.zohoUserId ?? ""}
								/>
							</nav>
						</>
					)}
				</div>

				{user && <UserInfo user={user} onLogout={logout} />}
			</aside>

			{/* Main chat area */}
			<main className="chat-area">
				{/* Mobile-only top bar with hamburger — hidden on desktop via CSS */}
				<button
					className="sidebar-hamburger"
					type="button"
					aria-label={sidebarOpen ? "Close navigation" : "Open navigation"}
					aria-expanded={sidebarOpen}
					onClick={() => setSidebarOpen(true)}
				>
					<span className="sidebar-hamburger-icon" aria-hidden="true">
						<span className="sidebar-hamburger-bar" />
						<span className="sidebar-hamburger-bar" />
						<span className="sidebar-hamburger-bar" />
					</span>
				</button>

				{activeView === "portfolio" ? (
					<PortfolioReport />
				) : (
					<>
						{user && (
							<ScopeBanner
								scopesValid={user.scopesValid ?? true}
								onLogout={logout}
							/>
						)}
						{(ingestionStatus || isSyncStarting) && (
							<IngestionBanner
								status={ingestionStatus ?? null}
								isSyncStarting={isSyncStarting}
							/>
						)}
						{contextWarning && (
							<ContextWarning warning={contextWarning} onDismiss={clearContextWarning} />
						)}

						{/* Setup wizard — replaces chat area when a project has no linked folder */}
						{shouldShowSetupWizard(selectedProject ?? null, wizardDismissedIds) ? (
							<ProjectSetupWizard
								key={selectedProjectId}
								project={selectedProject!}
								ingestionStatus={ingestionStatus}
								onComplete={handleWizardComplete}
								onSkip={handleWizardSkip}
							/>
						) : showDashboard && selectedProjectId ? (
							<ProjectDashboard
								projectId={selectedProjectId}
								onClose={() => setShowDashboard(false)}
							/>
						) : pendingNewChat && !activeSessionId ? (
							/* Title-input step — shown instead of the message list when creating a new chat */
							<NewChatTitleInput
								onSubmit={handleTitleSubmit}
								onCancel={handleTitleCancel}
								isCreating={isCreatingSession}
							/>
						) : (
							<>
								<ChatHeader
									title={currentSession?.title ?? "New chat"}
									projectName={selectedProject?.name}
									isShared={false}
									turns={messages.length}
									startedByName={currentSession?.creatorDisplayName}
								/>
								<ChatMessages
									messages={messages}
									isStreaming={isStreaming}
									user={user ?? undefined}
									onApproveAction={handleApproveAction}
									onRejectAction={handleRejectAction}
									scrollRef={chatMessagesRef}
									endRef={messagesEndRef}
									onScroll={handleChatScroll}
									emptyStateMessage={emptyStateMessage}
									showScrollTop={showScrollTop}
									showScrollBottom={showScrollBottom}
									onScrollTop={() => {
										chatMessagesRef.current?.scrollTo({ top: 0, behavior: "smooth" });
									}}
									onScrollBottom={() => {
										messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
									}}
								/>
								{activeSessionIsDigest ? (
									<DigestReadOnlyFooter />
								) : (
									<ChatInput
										onSend={sendMessage}
										disabled={isStreaming}
										inputRef={chatInputRef}
										projectId={selectedProjectId}
										onCommand={handleCommand}
									/>
								)}
							</>
						)}
					</>
				)}
			</main>
		</div>
	);
}
